import * as React from 'react';

import {useQuery} from '@apollo/react-hooks';

import {safelyGetGraphQLErrorMessage} from 'nutshell-core/utils/graphql-errors';

import type {BookingSortOrder} from 'nutshell-graphql-types';
import type {
    GetBookings as GetBookingsQuery,
    GetBookingsVariables as GetBookingsVariables,
    BookingsInput,
    BookingFragment,
} from 'nutshell-graphql-types';

import {nutApolloClient} from '../../../../apollo-client';

import GET_BOOKINGS from '../queries/get-bookings.graphql';

export const DEFAULT_PAGE_SIZE = 50;

export const useGetBookings = (
    input: BookingsInput,
    sortString?: string | null
): {
    bookings: BookingFragment[];
    numBookings: number;
    isLoading: boolean;
    error: string | null | undefined;
    refetch: () => Promise<any>;
    fetchNextPage: () => Promise<any>;
    fetchPreviousPage: () => Promise<any>;
    fetchFirstPageWithNewSort: (newSort: string) => Promise<any>;
} => {
    const sort = getSort(sortString);
    const originalQueryVariables = {
        input: {
            ...input,
            first: DEFAULT_PAGE_SIZE,
            sort,
        },
    } as const;

    const [currentSort, setCurrentSort] = React.useState(sort);

    const {loading, data, error, refetch, fetchMore} = useQuery<
        GetBookingsQuery,
        GetBookingsVariables
    >(GET_BOOKINGS, {
        variables: originalQueryVariables,
        fetchPolicy: 'cache-and-network',
        skip: !sort,
    });

    const bookings = data ? data.bookings : null;

    const allBookings = bookings ? bookings.edges.map((edge) => edge.node) : [];

    const pageInfo = bookings ? bookings.pageInfo : null;

    const handleFetchPreviousPage = () => {
        if (pageInfo && pageInfo.hasPreviousPage) {
            const nextRequestVariables = {
                input: {
                    ...input,
                    sort: currentSort,
                    before: pageInfo.startCursor,
                    last: DEFAULT_PAGE_SIZE,
                },
            } as const;

            return fetchMore({
                variables: nextRequestVariables,
                // @ts-expect-error
                updateQuery: (
                    prev: GetBookingsQuery,
                    newResults: {
                        fetchMoreResult: any;
                    }
                ) => {
                    // fetchMore will not cache this data under nextRequestVariables, so we need to do this manually
                    // so future calls with the same variables can rely on the cache.
                    nutApolloClient.writeQuery({
                        query: GET_BOOKINGS,
                        variables: nextRequestVariables,
                        data: newResults.fetchMoreResult,
                    });

                    return newResults.fetchMoreResult;
                },
            });
        }

        return Promise.reject();
    };

    const handleFetchNextPage = () => {
        if (pageInfo && pageInfo.hasNextPage) {
            const nextRequestVariables = {
                input: {
                    ...input,
                    after: pageInfo.endCursor,
                    first: DEFAULT_PAGE_SIZE,
                    sort: currentSort,
                },
            } as const;

            return fetchMore({
                variables: nextRequestVariables,
                // @ts-expect-error
                updateQuery: (
                    prev: GetBookingsQuery,
                    newResults: {
                        fetchMoreResult: GetBookingsQuery;
                    }
                ) => {
                    nutApolloClient.writeQuery({
                        query: GET_BOOKINGS,
                        variables: nextRequestVariables,
                        data: newResults.fetchMoreResult,
                    });

                    return newResults.fetchMoreResult;
                },
            });
        }

        return Promise.reject();
    };

    const handleFetchFirstPageWithNewSort = (newSort: string) => {
        const newSortString = getSort(newSort);
        if (!newSortString) {
            return Promise.reject();
        }

        setCurrentSort(newSortString);

        const newRequestVariables = {
            input: {
                ...input,
                first: DEFAULT_PAGE_SIZE,
                sort: newSortString,
            },
        } as const;

        return fetchMore({
            variables: newRequestVariables,
            // @ts-expect-error
            updateQuery: (
                prev: GetBookingsQuery,
                newResults: {
                    fetchMoreResult: GetBookingsQuery;
                }
            ) => {
                nutApolloClient.writeQuery({
                    query: GET_BOOKINGS,
                    variables: newRequestVariables,
                    data: newResults.fetchMoreResult,
                });

                return newResults.fetchMoreResult;
            },
        });
    };

    return {
        bookings: allBookings,
        numBookings: bookings ? bookings.totalCount : 0,
        isLoading: loading,
        // @ts-expect-error
        error: safelyGetGraphQLErrorMessage(error),
        refetch,
        fetchNextPage: handleFetchNextPage,
        fetchPreviousPage: handleFetchPreviousPage,
        fetchFirstPageWithNewSort: handleFetchFirstPageWithNewSort,
    };
};

function getSort(sort?: string | null): BookingSortOrder | null | undefined {
    if (!sort) {
        return null;
    }

    return getSortOrderEnum(sort);
}

function getSortOrderEnum(sort: string): BookingSortOrder | null | undefined {
    switch (sort) {
        case SortOrder.DATE_BOOKED_ASC:
            return 'CREATED_TIME_ASC';
        case SortOrder.DATE_BOOKED_DESC:
            return 'CREATED_TIME_DESC';
        case SortOrder.DATE_SCHEDULED_ASC:
            return 'START_TIME_ASC';
        case SortOrder.DATE_SCHEDULED_DESC:
            return 'START_TIME_DESC';
        default:
            return null;
    }
}

export const SortOrder = {
    DATE_BOOKED_ASC: 'createdTime',
    DATE_BOOKED_DESC: '-createdTime',
    DATE_SCHEDULED_ASC: 'startTime',
    DATE_SCHEDULED_DESC: '-startTime',
} as const;
